
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { ProfileData } from "@/types/profile";
import { useQueryClient } from "@tanstack/react-query";

export const useProfileSave = () => {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const queryClient = useQueryClient();

  const saveProfile = async (profile: ProfileData) => {
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("No user ID found");

      // Clean up the keywords before saving
      const cleanKeywords = profile.search_keywords
        ? profile.search_keywords
            .split(',')
            .map(k => k.trim())
            .filter(k => k)
            .join(', ')
        : null;

      const { error } = await supabase
        .from("profiles")
        .update({
          company_name: profile.company_name,
          notification_email: profile.notification_email,
          email_notifications_enabled: profile.email_notifications_enabled,
          preferred_industries: profile.preferred_industries,
          preferred_countries: profile.preferred_countries,
          min_price_range: profile.min_price_range,
          max_price_range: profile.max_price_range,
          search_keywords: cleanKeywords,
          price_filter_enabled: profile.price_filter_enabled,
          price_filter_currency: profile.price_filter_currency,
          notification_frequency: profile.notification_frequency || "daily", // Ensure notification_frequency is saved
          updated_at: new Date().toISOString()
        })
        .eq("id", user.id);

      if (error) {
        console.error("Error in saveProfile:", error);
        throw error;
      }

      // Invalidate both queries but don't refetch automatically - we'll handle that explicitly
      await queryClient.invalidateQueries({ queryKey: ["profile"], refetchType: 'none' });
      await queryClient.invalidateQueries({ queryKey: ["tender-matches"], refetchType: 'none' });

      toast({
        title: "Success",
        description: "Profile updated successfully",
      });
    } catch (error: any) {
      console.error("Error in saveProfile:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to save changes",
        variant: "destructive",
      });
      throw error;
    } finally {
      setSaving(false);
    }
  };

  return {
    saving,
    saveProfile,
  };
};
